/**
 * FWTV Phase 4 admin — transcription + highlights + clips.
 * Rendered as the "Intelligence" tab of /admin/fwtv.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Brain, ScissorsSquare, Sparkles, Trash2, Plus, Play } from 'lucide-react';
import api from '@/lib/api';

const getErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join(' · ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return d || e?.message || 'Error';
};

const secs = (s) => {
  s = Math.max(0, Number(s) || 0);
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${ss < 10 ? '0' : ''}${ss}`;
};

export const Intelligence = () => {
  const qc = useQueryClient();
  const [sermonId, setSermonId] = useState('');
  const { data: sermonsData } = useQuery({
    queryKey: ['fwtv-sermons'],
    queryFn: async () => (await api.get('/fwtv/admin/sermons')).data,
  });
  const sermons = sermonsData?.sermons || [];
  const sermon = sermons.find((s) => s.id === sermonId);

  const { data: transcript, refetch: refetchTranscript } = useQuery({
    queryKey: ['fwtv-transcript', sermonId],
    queryFn: async () => {
      if (!sermonId) return null;
      try { return (await api.get(`/fwtv/admin/sermons/${sermonId}/transcript`)).data; }
      catch (e) { if (e?.response?.status === 404) return null; throw e; }
    },
    enabled: !!sermonId,
  });

  const { data: candData, refetch: refetchCands } = useQuery({
    queryKey: ['fwtv-highlights', sermonId],
    queryFn: async () => (await api.get(`/fwtv/admin/sermons/${sermonId}/highlights`)).data,
    enabled: !!sermonId && !!transcript,
  });
  const { data: clipsData, refetch: refetchClips } = useQuery({
    queryKey: ['fwtv-clips', sermonId],
    queryFn: async () => (await api.get(`/fwtv/admin/sermons/${sermonId}/clips`)).data,
    enabled: !!sermonId,
  });

  const [transcribing, setTranscribing] = useState(false);
  const transcribe = async (force = false) => {
    if (!sermonId) return toast.error('Pick a sermon first');
    setTranscribing(true);
    toast.info('Starting transcription — this can take 30–90s for a typical sermon.');
    try {
      const res = await api.post(`/fwtv/admin/sermons/${sermonId}/transcribe`, { force });
      toast.success(res.data.message || 'Transcript ready');
      refetchTranscript();
    } catch (e) { toast.error(getErr(e)); }
    finally { setTranscribing(false); }
  };

  const deleteTranscript = async () => {
    if (!window.confirm('Delete transcript? (Clips are not affected.)')) return;
    try {
      await api.delete(`/fwtv/admin/sermons/${sermonId}/transcript`);
      toast.success('Transcript removed');
      qc.invalidateQueries({ queryKey: ['fwtv-transcript', sermonId] });
      qc.invalidateQueries({ queryKey: ['fwtv-highlights', sermonId] });
    } catch (e) { toast.error(getErr(e)); }
  };

  const generateHighlights = async () => {
    try {
      const res = await api.post(`/fwtv/admin/sermons/${sermonId}/highlights`, { target_length_seconds: 45, top_n: 6 });
      toast.success(`${res.data.count} highlight candidate(s) generated`);
      refetchCands();
    } catch (e) { toast.error(getErr(e)); }
  };

  const saveAsClip = async (c) => {
    try {
      await api.post(`/fwtv/admin/sermons/${sermonId}/clips`, {
        title: c.text ? c.text.slice(0, 60) : 'Highlight',
        description: c.text || '',
        start_seconds: c.start, end_seconds: c.end,
        is_published: false, // iter-438k: default to draft — send through review before public
        tags: ['auto'],
      });
      toast.success('Clip saved as draft — publish from the clips list when ready');
      refetchClips();
    } catch (e) { toast.error(getErr(e)); }
  };

  const deleteClip = async (cid) => {
    if (!window.confirm('Delete clip?')) return;
    try {
      await api.delete(`/fwtv/admin/clips/${cid}`);
      refetchClips();
    } catch (e) { toast.error(getErr(e)); }
  };

  // Manual clip form state
  const [clipForm, setClipForm] = useState({ title: '', start: '', end: '', description: '' });
  const createManualClip = async () => {
    if (!sermonId || !clipForm.title || clipForm.start === '' || clipForm.end === '') {
      return toast.error('Title, start, end are required');
    }
    try {
      await api.post(`/fwtv/admin/sermons/${sermonId}/clips`, {
        title: clipForm.title, description: clipForm.description,
        start_seconds: Number(clipForm.start), end_seconds: Number(clipForm.end),
        is_published: false, // iter-438k: publish explicitly from the row toggle
      });
      toast.success('Clip saved as draft');
      setClipForm({ title: '', start: '', end: '', description: '' });
      refetchClips();
    } catch (e) { toast.error(getErr(e)); }
  };

  // iter-438k: inline edit + publish toggle wired to PUT /admin/clips/{cid}
  const [editingClip, setEditingClip] = useState(null);
  const updateClip = async (cid, patch) => {
    try {
      await api.put(`/fwtv/admin/clips/${cid}`, patch);
      toast.success('Clip updated');
      setEditingClip(null);
      refetchClips();
    } catch (e) { toast.error(getErr(e)); }
  };
  const togglePublish = (c) => updateClip(c.id, { is_published: !c.is_published });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Brain className="h-4 w-4" /> Sermon transcription (OpenAI Whisper-1)</CardTitle>
          <CardDescription className="text-xs">Whisper-1 via Emergent LLM key. Files &gt;24MB are auto-split with ffmpeg. Duration on the sermon is backfilled from the transcript when missing.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Sermon</Label>
              <select className="w-full border rounded h-9 px-2 text-sm" value={sermonId} onChange={(e) => setSermonId(e.target.value)} data-testid="fwtv-intel-sermon-select">
                <option value="">— pick a sermon —</option>
                {sermons.map((s) => (
                  <option key={s.id} value={s.id}>{s.title}{s.status !== 'published' ? ' (draft)' : ''}</option>
                ))}
              </select>
              {sermon && (
                <p className="text-xs text-slate-500 mt-1">
                  {sermon.audio_url ? <span>audio ✓</span> : <span>no audio_url</span>}
                  {sermon.video_url ? <span> · video ✓</span> : null}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 justify-end">
              <div className="flex gap-2">
                <Button onClick={() => transcribe(false)} disabled={!sermonId || transcribing} data-testid="fwtv-transcribe-btn">
                  {transcribing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                  Transcribe
                </Button>
                {transcript && <Button variant="outline" onClick={() => transcribe(true)} disabled={transcribing} data-testid="fwtv-retranscribe-btn">Re-run</Button>}
                {transcript && <Button variant="ghost" className="text-red-600" onClick={deleteTranscript} data-testid="fwtv-transcript-del">Delete</Button>}
              </div>
              {transcript && (
                <p className="text-xs text-slate-500">
                  Status: <Badge variant="outline" className="text-[10px]">{transcript.status}</Badge>
                  {transcript.duration_seconds ? <> · {secs(transcript.duration_seconds)}</> : null}
                  {transcript.segments?.length ? <> · {transcript.segments.length} segments</> : null}
                </p>
              )}
            </div>
          </div>

          {transcript?.status === 'ready' && (
            <div className="border rounded p-3 max-h-72 overflow-y-auto bg-slate-50" data-testid="fwtv-transcript-viewer">
              <ol className="text-xs space-y-1">
                {(transcript.segments || []).slice(0, 200).map((s, idx) => (
                  <li key={idx} className="flex gap-2">
                    <span className="text-slate-400 font-mono min-w-[3rem]">{secs(s.start)}</span>
                    <span>{s.text}</span>
                  </li>
                ))}
                {(transcript.segments || []).length > 200 && <li className="text-slate-400">… truncated ({transcript.segments.length - 200} more segments)</li>}
              </ol>
            </div>
          )}

          {transcript?.status === 'error' && (
            <p className="text-xs text-red-600" data-testid="fwtv-transcript-error">Transcription failed: {transcript.error}</p>
          )}
        </CardContent>
      </Card>

      {/* Highlights */}
      {transcript?.status === 'ready' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4" /> Highlight candidates</CardTitle>
            <CardDescription className="text-xs">Auto-suggested 20–90s clips ranked by keyword density (Amen, Hallelujah, scripture refs, etc.). Click "Save as clip" to promote one.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button size="sm" variant="outline" onClick={generateHighlights} data-testid="fwtv-highlights-generate"><Sparkles className="h-3 w-3 mr-1" /> Generate</Button>
            {(candData?.candidates || []).length === 0 ? (
              <p className="text-xs text-slate-400 py-2">No candidates yet — click Generate.</p>
            ) : (
              <ul className="space-y-2">
                {candData.candidates.map((c) => (
                  <li key={c.id} className="border rounded p-2 text-xs" data-testid={`fwtv-candidate-${c.id}`}>
                    <div className="flex justify-between items-center">
                      <span className="font-mono text-slate-500">{secs(c.start)} → {secs(c.end)} · {Math.round(c.duration)}s · score {c.score}</span>
                      <Button size="sm" variant="outline" onClick={() => saveAsClip(c)} data-testid={`fwtv-candidate-save-${c.id}`}><Plus className="h-3 w-3 mr-1" /> Save as clip</Button>
                    </div>
                    <p className="text-slate-600 mt-1">{c.text}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Clips CRUD */}
      {sermonId && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><ScissorsSquare className="h-4 w-4" /> Clips</CardTitle>
            <CardDescription className="text-xs">Manually add in/out points, or promote highlight candidates above. Public embed: <code>{'<div data-fwtv="clips" data-sermon-slug="…"></div>'}</code></CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
              <Input placeholder="Clip title" value={clipForm.title} onChange={(e) => setClipForm({ ...clipForm, title: e.target.value })} data-testid="fwtv-clip-title" />
              <Input placeholder="Start (s)" type="number" value={clipForm.start} onChange={(e) => setClipForm({ ...clipForm, start: e.target.value })} data-testid="fwtv-clip-start" />
              <Input placeholder="End (s)" type="number" value={clipForm.end} onChange={(e) => setClipForm({ ...clipForm, end: e.target.value })} data-testid="fwtv-clip-end" />
              <Textarea rows={1} placeholder="Description (optional)" value={clipForm.description} onChange={(e) => setClipForm({ ...clipForm, description: e.target.value })} data-testid="fwtv-clip-desc" />
              <Button onClick={createManualClip} data-testid="fwtv-clip-create"><Plus className="h-4 w-4 mr-1" /> Add clip</Button>
            </div>
            {(clipsData?.clips || []).length === 0 ? (
              <p className="text-xs text-slate-400 py-2">No clips yet.</p>
            ) : (
              <div className="border rounded overflow-x-auto"><table className="w-full text-xs">
                <thead className="bg-slate-50"><tr>
                  <th className="text-left p-2">Title</th>
                  <th className="text-left p-2">Window</th>
                  <th className="text-left p-2">Published</th>
                  <th /></tr></thead>
                <tbody>{clipsData.clips.map((c) => (
                  <tr key={c.id} className="border-t" data-testid={`fwtv-clip-row-${c.id}`}>
                    <td className="p-2">{c.title}</td>
                    <td className="p-2 font-mono">{secs(c.start_seconds)} → {secs(c.end_seconds)}</td>
                    <td className="p-2">{c.is_published ? <Badge variant="outline" className="text-emerald-700 border-emerald-300 text-[10px]">public</Badge> : <Badge variant="outline" className="text-[10px]">draft</Badge>}</td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => togglePublish(c)} data-testid={`fwtv-clip-publish-${c.id}`} title={c.is_published ? 'Unpublish' : 'Publish'}>
                        {c.is_published ? 'Unpublish' : 'Publish'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingClip(c)} data-testid={`fwtv-clip-edit-${c.id}`}>Edit</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteClip(c.id)} data-testid={`fwtv-clip-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}</tbody>
              </table></div>
            )}
          </CardContent>
        </Card>
      )}

      {/* iter-438k: edit-clip dialog */}
      {editingClip && (
        <ClipEditDialog
          clip={editingClip}
          onClose={() => setEditingClip(null)}
          onSave={(patch) => updateClip(editingClip.id, patch)}
        />
      )}
    </div>
  );
};

const ClipEditDialog = ({ clip, onClose, onSave }) => {
  const [form, setForm] = useState({
    title: clip.title || '',
    description: clip.description || '',
    start_seconds: clip.start_seconds ?? 0,
    end_seconds: clip.end_seconds ?? 0,
    is_published: !!clip.is_published,
  });
  const save = () => {
    if (!form.title || form.end_seconds <= form.start_seconds) {
      return toast.error('Title required and end must be greater than start');
    }
    onSave({
      title: form.title,
      description: form.description,
      start_seconds: Number(form.start_seconds),
      end_seconds: Number(form.end_seconds),
      is_published: form.is_published,
    });
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="fwtv-clip-edit-dialog">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">Edit clip</h3>
        <div className="space-y-2">
          <Label className="text-xs">Title</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="fwtv-clip-edit-title" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Start (s)</Label>
            <Input type="number" value={form.start_seconds} onChange={(e) => setForm({ ...form, start_seconds: e.target.value })} data-testid="fwtv-clip-edit-start" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">End (s)</Label>
            <Input type="number" value={form.end_seconds} onChange={(e) => setForm({ ...form, end_seconds: e.target.value })} data-testid="fwtv-clip-edit-end" />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Description</Label>
          <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="fwtv-clip-edit-desc" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} data-testid="fwtv-clip-edit-published" />
          Public on the embed
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="fwtv-clip-edit-cancel">Cancel</Button>
          <Button onClick={save} data-testid="fwtv-clip-edit-save">Save changes</Button>
        </div>
      </div>
    </div>
  );
};

export default Intelligence;
